'use strict';

import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {observer} from "mobx-react";
import classnames from "classnames";

import {FaExclamationTriangle} from "react-icons/fa";

import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import ListGroup from "react-bootstrap/ListGroup";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import SimpleState from "@uw-it-sis/lib-react/lib/SimpleState";
import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";

import pinnedSchedulesStore from "./PinnedSchedulesStore";
import scheduleBuilderOptionsStore from "./ScheduleBuilderOptionsStore";
import {buildEvents} from "./ScheduleBuilderUtils";
import scheduleBuilderStore from "./ScheduleBuilderStore";
import ScheduleBuilderOptionModal from "./ScheduleBuilderOptionModal";
import PinnedScheduleOptionStore from "./PinnedScheduleOptionStore";
import {MetricLabels} from "@uw-it-sis/lib-react-myplan/lib/Constants";

class PinnedScheduleOption extends Component {
    constructor(props) {
        super(props);
        this.props = props;

        this.state = {
            showDetails: false,
        }

        this.store = new PinnedScheduleOptionStore();
        this.analyticsStore = appStore.getAnalyticsStore();
        this.showDetails = this.showDetails.bind(this);
        this.hideDetails = this.hideDetails.bind(this);
    }

    componentDidMount() {
        let scheduleData = {
            id: this.props.schedule.id,
            activityHash: this.props.schedule.activityHash,
            events: buildEvents(this.props.schedule.activityOptions, scheduleBuilderStore.getInitialDate(), `P${this.props.number}`),
            isPinned: true,
            isScheduleOption: true, // Tells ScheduleBuilderOptionsList that schedule can be displayed (doesn't show reserved or registered)
            classNames: ['scheduleBuilder-option--pinned', 'cursor-pointer'],
            name: `Pinned Schedule ${this.props.number}`
        };

        this.store.mount(scheduleData);
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    // Show the schedule details in modal
    showDetails(e) {
        e.stopPropagation();
        const labels = this.analyticsStore.label(MetricLabels.appContextName,"SB-menu-pinned").get();
        this.analyticsStore.addEvent("SB-open-details",labels, {});
        this.setState({showDetails: true});
    }

    // Close the schedule details modal
    hideDetails() {
        this.setState({showDetails: false});
    }

    // Action to show/hide schedule within calendar grid
    toggleSchedule() {
        let show = !this.store.showSchedule;

        if (show && scheduleBuilderOptionsStore.getDisplayLimit() === 0 ) {
            scheduleBuilderOptionsStore.openDisplayLimitMessage();
        } else {
            this.store.toggleShowSchedule(show);
        }
    }

    // Action to delete an existing pinned schedule
    delete(e) {
        e.stopPropagation();
        // If the schedule is displayed on the calendar, remove it from view while deleting
        if (this.store.showSchedule) {
            this.store.toggleShowSchedule(false);
        }
        pinnedSchedulesStore.deletePinnedSchedule(this.props.schedule.id, "SB-Pinned-Schedule");
    }

    highlightOption(e) {
        let card = e.target.closest(".list-group-item.scheduleBuilder-pinned-item");

        switch (e.type) {
            case "mouseover":
                card.classList.add("scheduleBuilder-pinned-item--hover");
                break;
            case "mouseout":
                card.classList.remove("scheduleBuilder-pinned-item--hover");
                break;
        }
    }

    _buildDisplay() {
        let description = `Pinned Schedule ${this.props.number}`;

        let pinnedScheduleIssues;
        if (
            this.props.schedule.possibleErrors
            &&
            this.props.schedule.possibleErrors.hasOwnProperty("errorMessagesByCourseCd")
            &&
            !isEmpty(this.props.schedule.possibleErrors.errorMessagesByCourseCd)
        ) {
            let possibleErrors = this.props.schedule.possibleErrors;
            let scheduleIssueList = [];
            for (const courseCd in possibleErrors.errorMessagesByCourseCd) {
                for (const errorType in possibleErrors.errorMessagesByCourseCd[courseCd]) {
                    possibleErrors.errorMessagesByCourseCd[courseCd][errorType].map(issue => {
                        scheduleIssueList.push(issue);
                    });
                }
            }

            if (scheduleIssueList.length > 0) {
                pinnedScheduleIssues =
                    <HelpPopover icon={FaExclamationTriangle}
                                 title={`${description} has issues`}
                                 placement="top"
                                 className="text-warning ps-3 py-3 pe-2">
                        {scheduleIssueList.join(" ")}
                    </HelpPopover>;
            }
        }

        return (
            <>
                <ListGroup.Item className={classnames(
                    "scheduleBuilder-pinned-item rounded p-0",
                    {"scheduleBuilder-pinned-item--active": this.store.showSchedule}
                )}>
                    <div className="d-flex align-items-stretch">
                        {pinnedScheduleIssues}
                        <button className={classnames(
                                    "border-0 w-100 bg-transparent text-start py-3 pe-3 ps-2 pinned-title h5 mb-0",
                                    {"ps-2": pinnedScheduleIssues},
                                    {"ps-3": !pinnedScheduleIssues}
                                )}
                                role="button"
                                type="button"
                                aria-pressed={this.store.showSchedule}
                                onMouseOver={this.highlightOption}
                                onMouseOut={this.highlightOption}
                                onClick={() => this.toggleSchedule()}>{description}</button>
                    </div>
                    <ButtonToolbar className="btn-toolbar-sm pt-1 px-3 pb-3">
                        <Button variant="secondary"
                                className="btn-secondary-gray"
                                size="sm"
                                title={`View ${description} Details`}
                                onClick={(e) => this.showDetails(e)}>
                            <small>View Details</small>
                        </Button>
                        <Button variant="secondary"
                                className="btn-secondary-gray"
                                size="sm"
                                title={`Delete ${description}`}
                                disabled={appStore.isAdviser()}
                                onClick={(e) => this.delete(e)}>
                            <small>Unpin</small>
                        </Button>
                    </ButtonToolbar>
                </ListGroup.Item>
                <ScheduleBuilderOptionModal
                    schedule={this.props.schedule}
                    title={description}
                    show={this.state.showDetails}
                    onHide={this.hideDetails}
                    isPinned
                    isFromPinnedMenu
                    regDate={this.props.regDate}
                    planData={[]}/>
            </>
        );

    }

    render() {
        return <SimpleState state={this.store.state} doneComponentBuilder={() => this._buildDisplay()}/>;
    }
}

PinnedScheduleOption.propTypes = {
    schedule: PropTypes.object.isRequired,
    number: PropTypes.number.isRequired,
    regDate: PropTypes.string.isRequired
};

export default observer(PinnedScheduleOption);